import React, { useState } from 'react';
import { FieldGroup, Modal, PillButton, Row, Stack, Text, TextInput } from 'library/ui';
import useLoading from 'library/hooks/useLoading';
import { client } from 'shared';
import { ShiftProviderUser } from 'models';

interface RevealData {
    full_name: string;
    ssn: string;
    dob: string;
    place_of_birth: string;
    address: {
        street: string;
        city: string;
        state: string;
        zip: string;
    };
    phone: string;
    email: string;
    sex: string;
    race: string;
    height: number;
    weight: number;
    hair_color: string;
    eye_color: string;
    prior_states: string[];
}

const DisplayData = ({ data }: { data: RevealData }) => {
    return (
        <Stack gap="sm">
            <Text>{data.full_name}</Text>
            <Text>DOB: {data.dob}</Text>
            <Text>SSN: {data.ssn}</Text>
            <Text>Place of Birth: {data.place_of_birth}</Text>
            <Text>
                Address: {data.address.street}, {data.address.city}, {data.address.state} {data.address.zip}
            </Text>
            <Text>Phone: {data.phone}</Text>
            <Text>Email: {data.email}</Text>
            <Text>Sex: {data.sex}</Text>
            <Text>Race: {data.race}</Text>
            <Text>Height: {data.height}</Text>
            <Text>Weight: {data.weight}</Text>
            <Text>Hair Color: {data.hair_color}</Text>
            <Text>Eye Color: {data.eye_color}</Text>
            <Text>States Resided In Past 5 Years: {data.prior_states.join(', ')}</Text>
        </Stack>
    );
};

export default function RevealModal({
    open,
    onClose,
    provider,
}: {
    open: boolean;
    onClose: () => void;
    provider: ShiftProviderUser;
}) {
    const { loading, setLoading } = useLoading();
    const [code, setCode] = useState<string>('');
    const [data, setData] = useState<RevealData>();
    const [error, setError] = useState<string>();
    function submit() {
        setError(undefined);
        setLoading(true);
        client(`api/business/reveal/`, { method: 'POST', body: { provider: provider.id, code: code } })
            .then((response) => {
                setData(response);
            })
            .catch((error) => {
                setError(error['error']);
            })
            .finally(() => {
                setLoading(false);
            });
    }
    const title = `Reveal ${provider.first_name} ${provider.last_name}'s SSN?`;
    const body =
        "You will receive a text message with a code to access the provider's SSN. If your number cannot receive texts you will get an email. Please enter the code here. Kindly note that this information is for roster purposes only.";

    if (!open) {
        return null;
    }

    return (
        <Modal
            title={title}
            onClose={() => {
                setData(undefined);
                onClose();
            }}
            width={500}
        >
            <Stack gap="md">
                <Text tone="secondary">{body}</Text>
                {!data && (
                    <FieldGroup label="Code">
                        <TextInput value={code} onChange={setCode} fullWidth />
                    </FieldGroup>
                )}
                {data ? (
                    <DisplayData data={data} />
                ) : (
                    <Row justify="end" fullWidth>
                        <PillButton disabled={loading} onClick={submit}>
                            {loading ? 'Revealing…' : 'Reveal'}
                        </PillButton>
                    </Row>
                )}
                {error && <Text tone="error">{error}</Text>}
            </Stack>
        </Modal>
    );
}
